/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import {
  AbstractFormComponent,
  CommonActions,
  FormActionPipelines,
  FormFlowState,
} from '@gh/builder-shared';
import { CheckboxTreeComponent, CheckboxTreeNode } from '@gh/common-internal-shared';
import { ActionFlow, getRouteInitData, MutablePageState } from '@gh/common-shared';
import { DialogService, FormMode, NotificationService } from '@gh/core-ui';
import { OfficeRolePermissionNode } from '../../../manager/resources/office-role-permission-tree.model';
import {
  buildCheckboxTreeModel,
  processEntityTree,
  processModelSubtree,
} from '../../../utils/permission-tree-utils';
import { MasterZone } from '../../resources/master-zone.model';
import { OfficeModulePermissionDetailsBusinessService } from '../../resources/office-module-permission-details-business.service';
import {
  MasterZoneModulePermissionNode,
  MasterZoneModulePermissionTree,
} from '../../resources/office-module-permission-tree.model';

@Component({
  standalone: false,
  selector: 'gh-office-module-permissions-details',
  templateUrl: 'office-module-permissions-details.component.html',
  styleUrls: ['office-module-permissions-details.component.scss'],
})
export class OfficeModulePermissionsDetailsComponent extends AbstractFormComponent<MasterZoneModulePermissionTree> implements OnInit {
  permissionCheckboxTreeModel: CheckboxTreeNode<MasterZoneModulePermissionNode>[] = [];

  masterZone: MasterZone;

  @ViewChild(CheckboxTreeComponent, { static: false }) tree: CheckboxTreeComponent<OfficeRolePermissionNode>;

  form: MutablePageState<MasterZoneModulePermissionTree>;

  constructor(private route: ActivatedRoute,
              private dialogService: DialogService,
              private notificationService: NotificationService,
              private pipelines: FormActionPipelines,
              private commonActions: CommonActions,
              private officePermissionsBusiness: OfficeModulePermissionDetailsBusinessService) {
    super();
  }

  get completeFlow(): ActionFlow<FormFlowState<MasterZoneModulePermissionTree>> {
    return this.pipelines.complete({
      execute: this.commonActions.execute(({ value }) => this.officePermissionsBusiness.updateModulePermissions(value)),
      finish: ({ value }) => {
        this.permissionCheckboxTreeModel = buildCheckboxTreeModel(value.roots, (node) => node.module);
        this.form.setValue(value);
        this.form.resetDirty();
      },
    });
  }

  ngOnInit(): void {
    const { masterZone, permissionTree } = getRouteInitData(this.route);

    this.masterZone = masterZone;
    this.permissionCheckboxTreeModel = buildCheckboxTreeModel(permissionTree.roots, (node) => node.module);

    this.form = new MutablePageState(FormMode.EditMode, permissionTree);
  }

  onExpandAll(): void {
    this.tree.expandAll();
  }

  onCollapseAll(): void {
    this.tree.collapseAll();
  }

  onPermissionChange(rootNode: CheckboxTreeNode<MasterZoneModulePermissionNode>): void {
    this.form.setDirty();

    processModelSubtree(rootNode, (node) => {
      const checked = node.checked;

      node.value.allowed = checked;

      if (node === rootNode) {
        processEntityTree(node.value, (child) => {
          if (!child.module) {
            child.allowed = checked;
          }
        });
      }
    });
  }
}
